import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'

import { locationDisplayText } from '../../../lib/location'
import { formatPhoneNumberForSave } from '../../../lib/phone'
import { round2 } from '../../../lib/sponsorPledgeFlowMath'
import { ensureAbsoluteUrl } from '../../../utils/url'
import {
  ALLOCATION_TOLERANCE,
  DEFAULT_SLIDER_PCT,
  PAYMENT_DEADLINE_DAYS,
} from './sponsorPledgeConstants'
import {
  formatPhoneInput,
  parseCurrencyCentsInput,
} from './sponsorPledgeFormat'
import {
  computeEstimatedVolunteerHours,
  formatEstimatedVolunteerHours,
} from './sponsorPledgeEventStats'
import { buildCreateCivPledgeRequestBody } from './sponsorPledgePayload'
import {
  computeReviewFeeTotals,
  platformFeesReady,
} from './sponsorPledgeTotals'
import { getStep3FieldErrors } from './sponsorPledgeValidation'

export function useSponsorPledgeWizardState({
  event,
  minimumPledgeCiv,
  business,
  currentUser,
  platformFees,
  t,
  createTmpSponsorshipPledge,
  updateBusiness,
}) {
  const [step, setStep] = useState(1)
  const [phase, setPhase] = useState('wizard')

  const [commitmentInput, setCommitmentInput] = useState('')
  const [vpfAmount, setVpfAmount] = useState(0)
  const [mpfAmount, setMpfAmount] = useState(0)
  const [vpfInputDraft, setVpfInputDraft] = useState('')
  const [mpfInputDraft, setMpfInputDraft] = useState('')
  const [isEditingVpf, setIsEditingVpf] = useState(false)
  const [isEditingMpf, setIsEditingMpf] = useState(false)
  const [sliderPct, setSliderPct] = useState(DEFAULT_SLIDER_PCT)
  const [opsAmount, setOpsAmount] = useState('')

  const [bizName, setBizName] = useState('')
  const [contactName, setContactName] = useState('')
  const [bizEmail, setBizEmail] = useState('')
  const [bizPhone, setBizPhone] = useState('')
  const [bizWeb, setBizWeb] = useState('')

  const [orgMessage, setOrgMessage] = useState('')

  const [fieldErrors, setFieldErrors] = useState({})
  const [allocWarn, setAllocWarn] = useState(false)

  const [tosAccepted, setTosAccepted] = useState(false)
  const [stripePaymentType, setStripePaymentType] = useState('ach')
  const [stripeModalOpen, setStripeModalOpen] = useState(false)
  const [submittingPledge, setSubmittingPledge] = useState(false)

  const minPledge = minimumPledgeCiv ?? 0
  const commitmentNum = useMemo(() => {
    const n = parseInt(String(commitmentInput).replace(/\D/g, ''), 10)
    return Number.isFinite(n) ? n : 0
  }, [commitmentInput])

  const meetsMin =
    minPledge > 0 ? commitmentNum >= minPledge : commitmentNum > 0

  const formattedLocation = useMemo(() => {
    if (event?.remote) return t('event.remote')
    return locationDisplayText(event?.location)
  }, [event?.remote, event?.location, t])

  const organizerName = event?.user?.name ?? '—'
  const eventTitle = event?.title ?? ''
  const eventIdPath = event?.slug ?? event?.id
  const eventBasePath = `/events/${eventIdPath}`

  const volunteerCount = event?.volunteers_needed ?? 0
  const estimatedVolunteerHours = useMemo(
    () => formatEstimatedVolunteerHours(computeEstimatedVolunteerHours(event)),
    [event],
  )

  const prefillDone = useRef(false)
  useEffect(() => {
    if (prefillDone.current || !business) return
    prefillDone.current = true
    setBizName(business.name || '')
    setBizPhone(
      formatPhoneInput(String(business.phone || '').replace(/\D/g, '')),
    )
    setBizWeb(business.website_url || '')
    setBizEmail(currentUser?.email || '')
    setContactName(currentUser?.name || '')
  }, [business, currentUser])

  useEffect(() => {
    if (step === 1 && minPledge > 0 && commitmentInput === '') {
      setCommitmentInput(String(minPledge))
    }
  }, [step, minPledge, commitmentInput])

  const syncAllocFromCommitment = useCallback(
    pct => {
      if (!commitmentNum) return
      const v = round2((commitmentNum * pct) / 100)
      setVpfAmount(v)
      setMpfAmount(round2(commitmentNum - v))
      setSliderPct(pct)
    },
    [commitmentNum],
  )

  useEffect(() => {
    if (step !== 2 || !commitmentNum) return
    const sum = round2(vpfAmount + mpfAmount)
    setAllocWarn(Math.abs(sum - commitmentNum) > ALLOCATION_TOLERANCE)
  }, [step, commitmentNum, vpfAmount, mpfAmount])

  const onEnterStep2 = useCallback(() => {
    if (!meetsMin) return
    syncAllocFromCommitment(DEFAULT_SLIDER_PCT)
    setStep(2)
  }, [meetsMin, syncAllocFromCommitment])

  const onSlider = useCallback(
    e => {
      const pct = Number(e.target.value)
      syncAllocFromCommitment(pct)
    },
    [syncAllocFromCommitment],
  )

  const onVpfInput = useCallback(
    e => {
      const parsed = parseCurrencyCentsInput(e.target.value)
      setVpfInputDraft(parsed == null ? '' : parsed.toFixed(2))
      if (!commitmentNum || parsed == null) return
      const clamped = Math.max(0, Math.min(commitmentNum, parsed))
      setVpfAmount(clamped)
      setMpfAmount(round2(commitmentNum - clamped))
      setSliderPct(Math.round((clamped / commitmentNum) * 100))
    },
    [commitmentNum],
  )

  const onMpfInput = useCallback(
    e => {
      const parsed = parseCurrencyCentsInput(e.target.value)
      setMpfInputDraft(parsed == null ? '' : parsed.toFixed(2))
      if (!commitmentNum || parsed == null) return
      const clamped = Math.max(0, Math.min(commitmentNum, parsed))
      const nextVpf = round2(commitmentNum - clamped)
      setMpfAmount(clamped)
      setVpfAmount(nextVpf)
      setSliderPct(Math.round((nextVpf / commitmentNum) * 100))
    },
    [commitmentNum],
  )

  useEffect(() => {
    if (!isEditingVpf) setVpfInputDraft(vpfAmount.toFixed(2))
  }, [vpfAmount, isEditingVpf])

  useEffect(() => {
    if (!isEditingMpf) setMpfInputDraft(mpfAmount.toFixed(2))
  }, [mpfAmount, isEditingMpf])

  const {
    serviceAmt,
    serviceFeePct,
    opsNum,
    reviewSubtotal,
    preStripeForProcessing,
    achFee,
    cardFee,
    totalAch,
    totalCard,
  } = useMemo(
    () =>
      computeReviewFeeTotals({
        commitmentNum,
        opsAmount,
        platformFees,
      }),
    [commitmentNum, opsAmount, platformFees],
  )

  const totalDue = useMemo(
    () => (stripePaymentType === 'ach' ? totalAch : totalCard),
    [stripePaymentType, totalAch, totalCard],
  )

  const stripeFee = useMemo(
    () => (stripePaymentType === 'ach' ? achFee : cardFee),
    [stripePaymentType, achFee, cardFee],
  )

  const validateStep3 = useCallback(() => {
    const err = getStep3FieldErrors({
      bizName,
      contactName,
      bizEmail,
      bizPhone,
      bizWeb,
    })
    setFieldErrors(err)
    if (Object.keys(err).length === 0) setStep(4)
  }, [bizName, contactName, bizEmail, bizPhone, bizWeb])

  const applicationDateStr = useMemo(
    () =>
      moment().format(
        t('config.date_format', { defaultValue: 'MMMM D, YYYY' }),
      ),
    [t],
  )

  const paymentDeadlineStr = useMemo(
    () =>
      moment()
        .add(PAYMENT_DEADLINE_DAYS, 'days')
        .format(t('config.date_format', { defaultValue: 'MMMM D, YYYY' })),
    [t],
  )

  const completePledgeSubmit = useCallback(
    async stripePaymentMethodId => {
      if (!tosAccepted || !stripePaymentMethodId) return
      if (!event?.id || !business?.id) {
        window.alert(t('sponsorPledgeFlow.errors.missingContext'))
        return
      }
      if (!platformFeesReady(platformFees)) {
        window.alert(t('sponsorPledgeFlow.errors.feesNotReady'))
        return
      }

      setSubmittingPledge(true)
      try {
        const termsAcceptedAtIso = new Date().toISOString()

        const bizRes = await updateBusiness(business.id, {
          business: {
            name: bizName.trim(),
            phone: formatPhoneNumberForSave(bizPhone),
            website_url: ensureAbsoluteUrl(bizWeb),
            volunteer_pay_forward: true,
          },
        })
        if (bizRes?.error) {
          const msg =
            bizRes.error?.response?.data?.error ||
            bizRes.error?.message ||
            t('sponsorPledgeFlow.errors.updateBusiness')
          window.alert(msg)
          return
        }

        const pledgeBody = buildCreateCivPledgeRequestBody({
          eventId: event.id,
          stripePaymentMethodId,
          commitmentNum,
          vpfAmount,
          mpfAmount,
          opsNum,
          messageToEventOwner: orgMessage,
          termsAcceptedAtIso,
        })

        const pledgeRes = await createTmpSponsorshipPledge(pledgeBody)
        if (pledgeRes?.error) {
          const msg =
            pledgeRes.error?.response?.data?.error ||
            pledgeRes.error?.message ||
            t('sponsorPledgeFlow.errors.createPledge')
          window.alert(msg)
          return
        }

        setStripeModalOpen(false)
        setPhase('submitted')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        setSubmittingPledge(false)
      }
    },
    [
      tosAccepted,
      event?.id,
      business?.id,
      platformFees,
      updateBusiness,
      createTmpSponsorshipPledge,
      bizName,
      bizPhone,
      bizWeb,
      commitmentNum,
      vpfAmount,
      mpfAmount,
      opsNum,
      orgMessage,
      t,
    ],
  )

  const spinCommitment = useCallback(
    delta => {
      const base = commitmentNum || minPledge || 0
      const next = Math.max(minPledge || 0, base + delta)
      setCommitmentInput(String(next))
    },
    [commitmentNum, minPledge],
  )

  const thumbSrc = event?.media_attachments?.[0]?.thumbnail_url

  return {
    step,
    setStep,
    phase,
    commitmentInput,
    setCommitmentInput,
    commitmentNum,
    minPledge,
    meetsMin,
    spinCommitment,
    onEnterStep2,
    sliderPct,
    onSlider,
    allocWarn,
    vpfAmount,
    mpfAmount,
    onVpfInput,
    onMpfInput,
    vpfInputDraft,
    mpfInputDraft,
    setIsEditingVpf,
    setIsEditingMpf,
    setVpfInputDraft,
    setMpfInputDraft,
    opsAmount,
    setOpsAmount,
    bizName,
    setBizName,
    contactName,
    setContactName,
    bizEmail,
    setBizEmail,
    bizPhone,
    setBizPhone,
    bizWeb,
    setBizWeb,
    orgMessage,
    setOrgMessage,
    fieldErrors,
    tosAccepted,
    setTosAccepted,
    stripeModalOpen,
    setStripeModalOpen,
    submittingPledge,
    formattedLocation,
    organizerName,
    eventTitle,
    eventBasePath,
    volunteerCount,
    estimatedVolunteerHours,
    applicationDateStr,
    paymentDeadlineStr,
    stripePaymentType,
    setStripePaymentType,
    serviceAmt,
    serviceFeePct,
    opsNum,
    reviewSubtotal,
    preStripeForProcessing,
    achFee,
    cardFee,
    stripeFee,
    totalDue,
    totalAch,
    totalCard,
    validateStep3,
    completePledgeSubmit,
    thumbSrc,
  }
}
